import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

import { UtilityService } from '../../services/utility.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { ModulesService } from '../../../api/services/modules.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { FieldLabelRowComponent } from './field-label-row.component';

import type { Entry } from '../../../features/admin/modules/modules.model';

@Component({
  selector: 'formly-field-linked-value',
  template: `
    <div class="flex gap-1 items-center w-full" [class.is-linked]="isLinked() && hasValue()">
      <!--
        Width classes from field.className are already applied by formly to the host
        <formly-field>. Re-applying them here compounded them (a half-width field rendered at
        50% of 50%); the inner field just fills its host.
      -->
      <mat-form-field class="flex-1">
        <mat-label>
          <app-field-label-row
            [label]="props.label + (props.required ? ' *' : '')"
            [required]="props.required"
            [info]="props.info"></app-field-label-row>
        </mat-label>
        <textarea
          #textarea
          matInput
          cdkTextareaAutosize
          [cdkAutosizeMinRows]="props.rows || 4"
          [cdkAutosizeMaxRows]="20"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [formlyDataCy]="field"
          [readonly]="isLinked()"
          [placeholder]="props.placeholder || ''">
        </textarea>
        <span matSuffix class="dd-clear-suffix" [class.hidden]="isLinked()">
          <app-clear-field-button
            [field]="field"
            [hasValue]="hasClearableValue()"
            [disabled]="isDisabled()"
            [readOnly]="isReadOnly() || isLinked()"
            [ariaLabel]="clearAriaLabel()"
            [focusTarget]="textarea"
            (clearSelection)="clear()" />
        </span>
        @if (isLinked() && hasValue() && !props.description) {
          <mat-hint>
            @if (sourceFieldLabel()) {
              Gekoppelde waarde van '{{ sourceFieldLabel() }}'
            } @else {
              Gekoppelde waarde
            }
          </mat-hint>
        }
        @if (props.description) {
          <mat-hint>{{ props.description }}</mat-hint>
        }
      </mat-form-field>
      <button
        mat-icon-button
        type="button"
        class="shrink-0 transition-opacity duration-200"
        [class.opacity-0]="!hasValue()"
        [class.pointer-events-none]="!hasValue()"
        [disabled]="isLoading() || isDisabled() || isReadOnly()"
        [attr.aria-label]="
          isLinked()
            ? sourceFieldLabel()
              ? 'Ontkoppelen van ' + sourceFieldLabel()
              : 'Ontkoppelen waarde'
            : sourceFieldLabel()
              ? 'Koppelen aan ' + sourceFieldLabel()
              : 'Waarde koppelen'
        "
        [matTooltip]="
          isLinked()
            ? sourceFieldLabel()
              ? 'Ontkoppelen van ' + sourceFieldLabel()
              : 'Ontkoppelen waarde'
            : sourceFieldLabel()
              ? 'Koppelen aan ' + sourceFieldLabel()
              : 'Waarde koppelen'
        "
        (click)="toggleLinked()">
        @if (isLoading()) {
          <mat-progress-spinner diameter="16" mode="indeterminate" />
        } @else {
          <mat-icon
            svgIcon="feather:link-2"
            [class.text-primary]="isLinked()"
            [class.text-hint]="!isLinked()"
            class="icon-size-4" />
        }
      </button>
    </div>
    <app-formly-error [field]="field" />
  `,
  styles: `
    /* Visual disabled state when the value is linked to the source */
    .is-linked {
      ::ng-deep .mat-mdc-text-field-wrapper {
        background-color: rgba(0, 0, 0, 0.04);
      }

      textarea {
        cursor: not-allowed;
        color: rgba(0, 0, 0, 0.54);
      }
    }

    :host-context(.dark) .is-linked {
      ::ng-deep .mat-mdc-text-field-wrapper {
        background-color: rgba(255, 255, 255, 0.04);
      }

      textarea {
        color: rgba(255, 255, 255, 0.5);
      }
    }
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatHint,
    MatInput,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatProgressSpinner,
    CdkTextareaAutosize,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyLinkedValueComponent extends ClearableFieldType<any> implements OnInit, OnDestroy {
  readonly #modulesStore = inject(ModulesStore);
  readonly #modulesService = inject(ModulesService);
  readonly #fuseConfirmationService = inject(FuseConfirmationService);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();

  isLinked = signal(true);
  lastSourceId = signal<string | null>(null);
  isLoading = signal(false);
  hasValue = signal(false);
  sourceFieldLabel = signal<string | null>(null);

  ngOnInit(): void {
    // Track whether the form control has a value (for fade-in of the lock button)
    this.hasValue.set(!!this.formControl.value);
    this.formControl.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe((value: unknown) => this.hasValue.set(!!value));

    const sourceFieldKey = this.field.props?.sourceFieldKey;
    if (!sourceFieldKey) {
      this.isLinked.set(false);
      this.#changeDetectorRef.markForCheck();
      return;
    }

    // Extract the field key from a potential keyPath (e.g., 'entry.general.address' → 'address')
    const fieldKey = sourceFieldKey.includes('.') ? sourceFieldKey.split('.').pop() : sourceFieldKey;
    const siblingField = UtilityService.findClosestFieldByKey(this.field, fieldKey);

    if (!siblingField?.formControl) {
      this.isLinked.set(false);
      this.#changeDetectorRef.markForCheck();
      return;
    }

    // Store source field label for display in the hint
    this.sourceFieldLabel.set(siblingField.props?.label ?? null);

    // Determine initial linked state by comparing current value with source
    this.#initializeLinkedState(siblingField.formControl.value);

    // Watch source field for changes.
    // Use ID-based comparator so that rebuild-flickers (same content, new object
    // reference) are filtered out — only react when the actual source ID changes.
    siblingField.formControl.valueChanges
      .pipe(
        takeUntil(this.#destroy$),
        distinctUntilChanged((a, b) => {
          const aId = a && typeof a === 'object' && 'id' in a ? (a as { id: string }).id : null;
          const bId = b && typeof b === 'object' && 'id' in b ? (b as { id: string }).id : null;
          return aId === bId;
        })
      )
      .subscribe(value => this.#onSourceValueChanged(value));
  }

  ngOnDestroy(): void {
    // Cleanup handled by injectDestroy
  }

  toggleLinked(): void {
    if (this.isDisabled() || this.isReadOnly()) return;

    if (this.isLinked()) {
      this.#confirmUnlock();
    } else {
      this.#confirmRelock();
    }
  }

  #confirmUnlock(): void {
    const sourceLabel = this.sourceFieldLabel();
    const sourceRef = sourceLabel ? `de ${sourceLabel.toLowerCase()}` : 'de bron';

    const confirmation = this.#fuseConfirmationService.open({
      title: 'Veld ontkoppelen?',
      message: `Weet je zeker dat je dit veld wilt ontkoppelen? Hierdoor wordt de waarde niet meer automatisch bijgewerkt zodra ${sourceRef} wijzigt.`,
      icon: {
        show: true,
        name: 'heroicons_outline:lock-open',
        color: 'warn',
      },
      actions: {
        confirm: {
          show: true,
          label: 'Ontkoppelen',
          color: 'warn',
        },
        cancel: {
          show: true,
          label: 'Annuleren',
        },
      },
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.isLinked.set(false);
      }
    });
  }

  #confirmRelock(): void {
    const sourceLabel = this.sourceFieldLabel();
    const sourceRef = sourceLabel ? `${sourceLabel.toLowerCase()}` : 'bron';

    const confirmation = this.#fuseConfirmationService.open({
      title: 'Veld opnieuw koppelen?',
      message: `Als je het veld opnieuw koppelt, worden je handmatige aanpassingen verwijderd en wordt de standaardwaarde uit de ${sourceRef} geladen.`,
      icon: {
        show: true,
        name: 'heroicons_outline:exclamation-triangle',
        color: 'warn',
      },
      actions: {
        confirm: {
          show: true,
          label: 'Koppelen',
          color: 'primary',
        },
        cancel: {
          show: true,
          label: 'Annuleren',
        },
      },
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.isLinked.set(true);
        const sourceId = this.lastSourceId();
        if (sourceId) {
          this.#fetchAndSetValue(sourceId);
        }
      }
    });
  }

  #onSourceValueChanged(value: unknown): void {
    // Extract source ID (or null if cleared/invalid)
    const newSourceId = value && typeof value === 'object' && 'id' in value ? (value as { id: string }).id : null;
    const previousSourceId = this.lastSourceId();

    // Same source ID → nothing to do (filters out rebuild flickers where the
    // sibling formControl gets a new instance with the same content)
    if (newSourceId === previousSourceId) {
      return;
    }

    // Init-race scenario: previousSourceId was null AND we already have a value.
    // This happens during route reorder rebuild — Formly populates values after
    // ngOnInit, so the first valueChanges emission looks like a "new source".
    // Use comparison logic instead of overwriting the user's saved edit.
    if (previousSourceId === null && this.formControl.value && newSourceId !== null) {
      this.lastSourceId.set(newSourceId);
      this.#checkIfLinked(newSourceId);
      return;
    }

    // Source was cleared (real, not init)
    if (newSourceId === null) {
      this.lastSourceId.set(null);
      this.formControl.setValue(null);
      this.isLinked.set(true);
      return;
    }

    // Real source change: defer the decision via microtask to detect whether
    // this is a reorder/programmatic update (where our own formControl ALSO gets
    // a new value via patchValue) vs a user-initiated source change (where only
    // the source changed but the formControl is still the old value).
    //
    // During a route reorder, sibling.valueChanges fires BEFORE our own
    // formControl.valueChanges. By queuing a microtask, we let all sync valueChanges
    // from the patchValue complete first, so we can compare formControl.value
    // before vs after.
    const valueBeforeMicrotask = this.formControl.value;
    const capturedNewSourceId = newSourceId;
    const capturedPreviousSourceId = previousSourceId;

    queueMicrotask(() => {
      const valueAfterMicrotask = this.formControl.value;
      const formControlAlsoChanged = valueBeforeMicrotask !== valueAfterMicrotask;

      // Guard against late callbacks: if another source change happened in the meantime
      // and lastSourceId no longer matches, skip — that newer change will handle it.
      if (this.lastSourceId() !== capturedPreviousSourceId && this.lastSourceId() !== capturedNewSourceId) {
        return;
      }

      this.lastSourceId.set(capturedNewSourceId);

      if (formControlAlsoChanged) {
        // Reorder/programmatic update: both source AND value changed in the same patch.
        // The new value might already match the new source's template (if linked at the
        // origin position) or not (if there was a user edit at the origin position).
        // Let checkIfLinked decide WITHOUT overwriting the value.
        this.#checkIfLinked(capturedNewSourceId);
      } else {
        // Only source changed → user picked a different option in the autocomplete.
        // Overwrite with the new source's rendered template and re-link.
        this.isLinked.set(true);
        this.#fetchAndSetValue(capturedNewSourceId);
      }
    });
  }

  #initializeLinkedState(sourceValue: unknown): void {
    if (!sourceValue || typeof sourceValue !== 'object' || !('id' in sourceValue)) {
      // No source value → unlinked
      if (!this.formControl.value) {
        this.isLinked.set(true); // Empty state, default linked
      } else {
        this.isLinked.set(false); // Has a value but no source → unlinked
      }
      return;
    }

    const sourceId = (sourceValue as { id: string }).id;
    this.lastSourceId.set(sourceId);

    // Compare current value with source value to determine linked state
    if (!this.formControl.value) {
      this.#fetchAndSetValue(sourceId);
    } else {
      this.#checkIfLinked(sourceId);
    }
  }

  #checkIfLinked(sourceId: string): void {
    const moduleCode = this.#getSourceModuleCode();
    if (!moduleCode) {
      this.isLinked.set(false);
      return;
    }

    const sourceModule = this.#modulesStore.getModuleByCode(moduleCode);
    if (!sourceModule) {
      this.isLinked.set(false);
      return;
    }

    this.#modulesService
      .getModuleEntry({ moduleId: sourceModule.id, entryId: sourceId })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: fullEntry => {
          const renderedValue = this.#renderTemplate(fullEntry as Entry);
          const currentValue = this.formControl.value;
          const matches = currentValue === renderedValue;
          this.isLinked.set(matches);
        },
        error: () => {
          this.isLinked.set(false);
        },
      });
  }

  #fetchAndSetValue(sourceId: string): void {
    const moduleCode = this.#getSourceModuleCode();
    if (!moduleCode) {
      return;
    }

    const sourceModule = this.#modulesStore.getModuleByCode(moduleCode);
    if (!sourceModule) {
      return;
    }

    const sourceTemplate = this.field.props?.sourceTemplate;
    if (!sourceTemplate) {
      return;
    }

    this.isLoading.set(true);
    this.lastSourceId.set(sourceId);

    this.#modulesService
      .getModuleEntry({ moduleId: sourceModule.id, entryId: sourceId })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: fullEntry => {
          const renderedValue = this.#renderTemplate(fullEntry as Entry);
          this.formControl.setValue(renderedValue || null);
          this.formControl.markAsDirty();
          this.isLoading.set(false);
        },
        error: err => {
          console.warn('LinkedValue: Failed to fetch source entry', err);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Render the merge template by replacing {keyPath} placeholders with values from the source entry.
   *
   * Supports two syntaxes:
   * - Plain placeholder: `{entry.general.note}` — replaced with the value, or empty string if null
   * - Conditional block: `[Label: {entry.general.note}]` — the entire bracketed block (label + value)
   *   is hidden if all placeholders inside resolve to empty/null. Useful for conditional labels.
   *
   * Lines that become empty after rendering (e.g. a line containing only a hidden bracket) are
   * removed. Lines that were already empty in the template are preserved as intentional spacing.
   *
   * Example template:
   *   "[Vooraf: {entry.general.notitie-vooraf}]\n[Achteraf: {entry.general.notitie-achteraf}]"
   * If `notitie-vooraf` is empty and `notitie-achteraf` = "Tekenen", output:
   *   "Achteraf: Tekenen"
   */
  #renderTemplate(fullEntry: Entry): string {
    const template = this.field.props?.sourceTemplate as string | undefined;
    if (!template) return '';

    const lines = template.split('\n');
    const renderedLines = lines.map(line => {
      const hadContent = line.trim() !== '';

      // Step 1: process [bracketed] blocks — hide entire block if all placeholders inside are empty
      let processed = line.replace(/\[([^[\]]*)]/g, (_match, content: string) => {
        let hasValue = false;
        const rendered = content.replace(/\{([^}]+)}/g, (_m, keyPath: string) => {
          const value = UtilityService.keyPathToValue(fullEntry, keyPath.trim());
          if (value != null && value !== '') {
            hasValue = true;
            return String(value);
          }
          return '';
        });
        return hasValue ? rendered : '';
      });

      // Step 2: process remaining {placeholders} outside brackets
      processed = processed.replace(/\{([^}]+)}/g, (_m, keyPath: string) => {
        const value = UtilityService.keyPathToValue(fullEntry, keyPath.trim());
        return value != null ? String(value) : '';
      });

      const becameEmpty = hadContent && processed.trim() === '';
      return { processed, becameEmpty };
    });

    return renderedLines
      .filter(({ becameEmpty }) => !becameEmpty)
      .map(({ processed }) => processed)
      .join('\n');
  }

  #getSourceModuleCode(): string | undefined {
    const sourceFieldKey = this.field.props?.sourceFieldKey;
    if (!sourceFieldKey) return undefined;

    const fieldKey = sourceFieldKey.includes('.') ? sourceFieldKey.split('.').pop() : sourceFieldKey;
    const siblingField = UtilityService.findClosestFieldByKey(this.field, fieldKey);

    return siblingField?.props?.optionsArray?.[0]?.moduleCode;
  }
}
